import React, { PropsWithChildren } from "react";
import { useDebouncedValue } from "rooks";
import {
    Button,
    ButtonBase,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormLabel,
    Slide,
    Slider,
    Theme,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { ExpandMore } from "@mui/icons-material";
import { SxProps } from "@mui/system";

import { useResizeObserver } from "@iva/resize-component";

import { useAppDispatch, useAppSelector } from "#/store";
import {
    setComparisonWeight,
    setFavoriteWeight,
    setQueryWeight,
    setRelevanceWeight,
} from "#/store/ui";
import { CollapseableColumn, ParameterizedColumn } from "#/util/columnSorting";
import { FavoriteColumnBox, ReferenceQueryInput } from "./DoiHandles";
import {
    ClusterData,
    DoiMapMain,
    DoiMapSub,
    SingleTermMapProps,
} from "./DoiMap";

/**
 * Declarations
 */

export interface Props {
    columnOrder?: CollapseableColumn[];
    allAttributes?: ParameterizedColumn[][];
    mapSpacing?: number;
    minimumCellSize?: number;
    applyAutomatically?: boolean;
    disabled?: boolean;
    columnHoverCallback?: (columns: string[]) => void;
}

interface ExpandableLabelProps extends SingleTermMapProps {
    label: string;
    description: string;
}

interface PlaceholderButtonProps {
    applyButton: JSX.Element | null;
}

const RightAlign = styled("div")({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
});

const Placeholder = styled(ButtonBase)(({ theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-evenly",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    ":hover": {
        backgroundColor: theme.palette.action.focus,
    },
}));

const LabelRow = styled(ButtonBase)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    textAlign: "left",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
}));

const RowFill = styled("div")({
    flexGrow: 1,
});

const SpacedContainer = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: "100%",
}));

const expand: SxProps<Theme> = {
    transform: "rotate(0deg)",
    transition: theme =>
        theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
};

const expandOpen: SxProps<Theme> = {
    transform: "rotate(180deg)",
    transition: theme =>
        theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
};

const DialogTransition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const RELEVANCE_DESCRIPTION =
    "Relevance is based on the entropy of the column's content.";
const COMPARISON_DESCRIPTION =
    "Compares the sets of values across columns with the same name (i.e. vertically). The more similar two table columns are, the lower their relevance.";
const QUERY_DESCRIPTION =
    "Evaluates the query against the column headers. The more words in common, the higher a columns relevance.";
const FAVORITE_DESCRIPTION =
    "Allows to manipulate the influence of favorited columns to the column relevance.";

/**
 * Helper Functions
 */

/**
 * Computes the amount of columns within each cluster in the given ordering.
 * @param columnOrder the ordering
 * @returns the numbers of columns for each cluster
 */
function computeClusterSizes(columnOrder: CollapseableColumn[]): ClusterData[] {
    return columnOrder.reduce(
        (prev: ClusterData[], curr: CollapseableColumn, currId: number) => {
            if (curr.collapseable) {
                // increase last element by 1
                const newArray = [...prev];
                newArray[newArray.length - 1].count += 1;
                return newArray;
            } else {
                // append 1
                return [
                    ...prev,
                    {
                        start: currId,
                        count: 1,
                        doi: 0,
                    },
                ];
            }
        },
        []
    );
}

/**
 * Computes the merged doi of all clusters in the given cluster structure
 * @param clusterSizes the cluster structure
 * @param columnOrder the column ordering
 * @param attributes the column attributes
 * @returns the doi of all clusters
 */
function computeClusterDoi(
    clusterSizes: ClusterData[],
    columnOrder: CollapseableColumn[],
    attributes: ParameterizedColumn[][]
): ClusterData[] {
    return clusterSizes.map(cluster => {
        let clusterAttributes: ParameterizedColumn = {
            name: "",
            relevance: 0,
            comparison: 0,
            query: 0,
            favorite: 0,
        };
        // compute merged attributes of the cluster
        for (let i = cluster.start; i < cluster.start + cluster.count; ++i) {
            const colName = columnOrder[i].name;
            // find corresponding column attributes in all tables
            const columnAttributes: ParameterizedColumn[] = attributes
                .map(table => {
                    const idx = table.findIndex(d => d.name === colName);
                    if (idx !== -1) {
                        return table[idx];
                    } else {
                        return null;
                    }
                })
                .filter(d => d !== null) as ParameterizedColumn[];
            // merge found attributes with those of previous columns
            clusterAttributes = {
                name: "",
                relevance: Math.max(
                    clusterAttributes.relevance,
                    ...columnAttributes.map(d => d.relevance)
                ),
                comparison: Math.max(
                    clusterAttributes.comparison,
                    ...columnAttributes.map(d => d.comparison)
                ),
                query: Math.max(
                    clusterAttributes.query,
                    ...columnAttributes.map(d => d.query)
                ),
                favorite: Math.max(
                    clusterAttributes.favorite,
                    ...columnAttributes.map(d => d.favorite)
                ),
            };
        }
        return {
            start: cluster.start,
            count: cluster.count,
            doi:
                clusterAttributes.relevance +
                clusterAttributes.comparison +
                clusterAttributes.query +
                clusterAttributes.favorite,
        };
    });
}

function changeWrapper(
    fun: (d: number) => void
): (evt: Event, value: number | number[]) => void {
    return (evt: Event, value: number | number[]) => {
        if (typeof value === "number") {
            fun(value);
        } else {
            fun(value[0]);
        }
    };
}

/**
 * Component
 */

/**
 * This component contains a label with an expand button that will show the corresponding map when clicked.
 */
const ExpandableLabel: React.FC<ExpandableLabelProps> = props => {
    const [expanded, setExpanded] = React.useState(false);

    return (
        <>
            <Tooltip
                title={
                    props.description +
                    " Click to " +
                    (expanded ? "hide" : "show") +
                    " impact on DOI."
                }
                disableInteractive
            >
                <LabelRow
                    onClick={() => setExpanded(oldExpanded => !oldExpanded)}
                >
                    <RowFill>
                        <FormLabel
                            sx={{
                                cursor: "pointer",
                            }}
                        >
                            {props.label}
                        </FormLabel>
                    </RowFill>
                    <ExpandMore sx={expanded ? expandOpen : expand} />
                </LabelRow>
            </Tooltip>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <DoiMapSub
                    columnAttributes={props.columnAttributes}
                    columnOrder={props.columnOrder}
                    measure={props.measure}
                    spacing={props.spacing}
                    maxValue={props.maxValue}
                    minValue={props.minValue}
                    size={{ width: "100%" }}
                    columnHoverCallback={props.columnHoverCallback}
                />
            </Collapse>
        </>
    );
};

/**
 * This creates a placeholder button prompting the user to click it to open the configuration in a dialog.
 * Used if the configuration component is too small to properly display the columns.
 */
const PlaceholderButton: React.FC<
    PropsWithChildren<PlaceholderButtonProps>
> = props => {
    const [modalOpen, setModalOpen] = React.useState(false);

    return (
        <>
            <Placeholder onClick={() => setModalOpen(true)}>
                <Typography variant="caption">
                    Too many columns for this resolution.
                </Typography>
                <Typography variant="caption">
                    Click to show map and configuration options.
                </Typography>
            </Placeholder>
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                TransitionComponent={DialogTransition}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">DOI Configuration</Typography>
                </DialogTitle>
                <DialogContent>{props.children}</DialogContent>
                <DialogActions>
                    {props.applyButton}
                    <Button color="primary" onClick={() => setModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

/**
 * This is the main configuration component.
 * It works out of the box without passing any props.
 * If the columnOrder and attributes are given, DOI maps are displayed.
 */
export const DoiConfiguration: React.FC<Props> = ({
    columnOrder,
    allAttributes,
    mapSpacing = 1,
    minimumCellSize = 3,
    applyAutomatically = false,
    disabled = false,
    columnHoverCallback,
}) => {
    const dispatch = useAppDispatch();

    // watch for component resizing
    const [ref, size] = useResizeObserver();

    // store access
    const storeRelevance = useAppSelector(state => state.uiState.doiWeights[0]);
    const storeComparison = useAppSelector(
        state => state.uiState.doiWeights[1]
    );
    const storeQuery = useAppSelector(state => state.uiState.doiWeights[2]);
    const storeFavorite = useAppSelector(state => state.uiState.doiWeights[3]);

    // component state
    const [relevance, setRelevance] = React.useState(storeRelevance);
    const [comparison, setComparison] = React.useState(storeComparison);
    const [query, setQuery] = React.useState(storeQuery);
    const [favorite, setFavorite] = React.useState(storeFavorite);

    // update component state on store value change
    React.useEffect(() => {
        setRelevance(storeRelevance);
    }, [storeRelevance]);
    React.useEffect(() => {
        setComparison(storeComparison);
    }, [storeComparison]);
    React.useEffect(() => {
        setQuery(storeQuery);
    }, [storeQuery]);
    React.useEffect(() => {
        setFavorite(storeFavorite);
    }, [storeFavorite]);

    // slider update callbacks
    const updateRelevance =
        applyAutomatically === true
            ? (d: number) => dispatch(setRelevanceWeight(d))
            : (d: number) => setRelevance(d);
    const updateComparison =
        applyAutomatically === true
            ? (d: number) => dispatch(setComparisonWeight(d))
            : (d: number) => setComparison(d);
    const updateQuery =
        applyAutomatically === true
            ? (d: number) => dispatch(setQueryWeight(d))
            : (d: number) => setQuery(d);
    const updateFavorite =
        applyAutomatically === true
            ? (d: number) => dispatch(setFavoriteWeight(d))
            : (d: number) => setFavorite(d);

    // content of the configuration component
    let configurationContent: JSX.Element | null = null;

    const relevanceSlider = (
        <Slider
            aria-label="Relevance Weight"
            value={relevance}
            valueLabelDisplay="auto"
            step={0.01}
            min={-1}
            max={1}
            disabled={disabled}
            marks={[
                { value: -1, label: "--" },
                { value: -0.5, label: "-" },
                {
                    value: 0,
                    label: "Neutral",
                },
                { value: 0.5, label: "+" },
                { value: 1, label: "++" },
            ]}
            onChange={changeWrapper(updateRelevance)}
        />
    );

    const comparisonSlider = (
        <Slider
            aria-label="Comparison Weight"
            value={comparison}
            valueLabelDisplay="auto"
            step={0.01}
            min={-1}
            max={1}
            disabled={disabled}
            marks={[
                { value: -1, label: "--" },
                { value: -0.5, label: "-" },
                {
                    value: 0,
                    label: "Neutral",
                },
                { value: 0.5, label: "+" },
                { value: 1, label: "++" },
            ]}
            onChange={changeWrapper(updateComparison)}
        />
    );

    const querySlider = (
        <Slider
            aria-label="Query Weight"
            value={query}
            valueLabelDisplay="auto"
            step={0.01}
            min={-1}
            max={1}
            disabled={disabled}
            marks={[
                { value: -1, label: "--" },
                { value: -0.5, label: "-" },
                {
                    value: 0,
                    label: "Neutral",
                },
                { value: 0.5, label: "+" },
                { value: 1, label: "++" },
            ]}
            onChange={changeWrapper(updateQuery)}
        />
    );

    const favoriteSlider = (
        <Slider
            aria-label="Favorite Weight"
            value={favorite}
            valueLabelDisplay="auto"
            step={0.01}
            min={-1}
            max={1}
            disabled={disabled}
            marks={[
                { value: -1, label: "--" },
                { value: -0.5, label: "-" },
                {
                    value: 0,
                    label: "Neutral",
                },
                { value: 0.5, label: "+" },
                { value: 1, label: "++" },
            ]}
            onChange={changeWrapper(updateFavorite)}
        />
    );

    const applyButton =
        applyAutomatically !== true ? (
            <Button
                size="large"
                disabled={disabled}
                onClick={() => {
                    dispatch(setRelevanceWeight(relevance));
                    dispatch(setComparisonWeight(comparison));
                    dispatch(setQueryWeight(query));
                    dispatch(setFavoriteWeight(favorite));
                }}
            >
                Apply
            </Button>
        ) : null;

    // determine whether all necessary data for a map display was given
    if (
        columnOrder !== undefined &&
        columnOrder.length > 0 &&
        allAttributes !== undefined &&
        allAttributes.length > 0
    ) {
        // compute weighted attributes
        const weightedAttributes: ParameterizedColumn[][] = allAttributes.map(
            table =>
                table.map(col => ({
                    name: col.name,
                    relevance: col.relevance * relevance,
                    comparison: col.comparison * comparison,
                    query: col.query * query,
                    favorite: col.favorite * favorite,
                }))
        );

        // aggregate weighted attributes
        const columnDoi: number[][] = weightedAttributes.map(table =>
            table.map(
                col => col.relevance + col.comparison + col.query + col.favorite
            )
        );

        // compute cluster structure
        const clusterSizes = computeClusterSizes(columnOrder);

        // compute cluster doi
        const clusterDoi = computeClusterDoi(
            clusterSizes,
            columnOrder,
            weightedAttributes
        );

        // compute value domain
        const allValues = [
            ...columnDoi.flat(),
            ...clusterDoi.map(d => d.doi),
            ...weightedAttributes.flatMap(table =>
                table.flatMap(col => [
                    col.relevance,
                    col.comparison,
                    col.query,
                    col.favorite,
                ])
            ),
        ];
        const maxVal = Math.max(...allValues, 1.0);
        const minVal = Math.min(...allValues, -1);

        // create map-display elements
        const mainMap = (
            <SpacedContainer>
                <DoiMapMain
                    columnDoi={columnDoi}
                    clusterDoi={clusterDoi}
                    columnAttributes={weightedAttributes}
                    columnOrder={columnOrder}
                    spacing={mapSpacing}
                    maxValue={maxVal}
                    minValue={minVal}
                    size={{ width: "100%" }}
                    columnHoverCallback={columnHoverCallback}
                />
            </SpacedContainer>
        );

        const relevanceLabel = (
            <ExpandableLabel
                columnAttributes={weightedAttributes}
                columnOrder={columnOrder}
                measure="relevance"
                spacing={mapSpacing}
                maxValue={maxVal}
                minValue={minVal}
                columnHoverCallback={columnHoverCallback}
                label="Relevance"
                description={RELEVANCE_DESCRIPTION}
            />
        );

        const comparisonLabel = (
            <ExpandableLabel
                columnAttributes={weightedAttributes}
                columnOrder={columnOrder}
                measure="comparison"
                spacing={mapSpacing}
                maxValue={maxVal}
                minValue={minVal}
                columnHoverCallback={columnHoverCallback}
                label="Comparison"
                description={COMPARISON_DESCRIPTION}
            />
        );

        const queryLabel = (
            <ExpandableLabel
                columnAttributes={weightedAttributes}
                columnOrder={columnOrder}
                measure="query"
                spacing={mapSpacing}
                maxValue={maxVal}
                minValue={minVal}
                columnHoverCallback={columnHoverCallback}
                label="Query Overlap"
                description={QUERY_DESCRIPTION}
            />
        );

        const favoriteLabel = (
            <ExpandableLabel
                columnAttributes={weightedAttributes}
                columnOrder={columnOrder}
                measure="favorite"
                spacing={mapSpacing}
                maxValue={maxVal}
                minValue={minVal}
                columnHoverCallback={columnHoverCallback}
                label="Favorites"
                description={FAVORITE_DESCRIPTION}
            />
        );

        // determine whether a placeholder should be displayed instead
        let sufficientScreenSpace = true;
        if (size !== undefined) {
            const cellSize =
                (size.width - (columnOrder.length - 1) * mapSpacing) /
                columnOrder.length;
            if (cellSize < minimumCellSize) {
                sufficientScreenSpace = false;
            }
        }

        const body = (
            <>
                {mainMap}
                {relevanceLabel}
                {relevanceSlider}
                {comparisonLabel}
                {comparisonSlider}
                {queryLabel}
                <ReferenceQueryInput />
                {querySlider}
                {favoriteLabel}
                <FavoriteColumnBox />
                {favoriteSlider}
            </>
        );

        if (sufficientScreenSpace) {
            configurationContent = (
                <>
                    {body}
                    <RightAlign>{applyButton}</RightAlign>
                </>
            );
        } else {
            configurationContent = (
                <PlaceholderButton applyButton={applyButton}>
                    {body}
                </PlaceholderButton>
            );
        }
    } else {
        // sliders without maps
        const relevanceLabel = (
            <Tooltip title={RELEVANCE_DESCRIPTION} disableInteractive>
                <FormLabel>Relevance</FormLabel>
            </Tooltip>
        );

        const comparisonLabel = (
            <Tooltip title={COMPARISON_DESCRIPTION} disableInteractive>
                <FormLabel>Comparison</FormLabel>
            </Tooltip>
        );

        const queryLabel = (
            <Tooltip title={QUERY_DESCRIPTION} disableInteractive>
                <FormLabel>Query Overlap</FormLabel>
            </Tooltip>
        );

        const favoriteLabel = (
            <Tooltip title={FAVORITE_DESCRIPTION} disableInteractive>
                <FormLabel>Favorites</FormLabel>
            </Tooltip>
        );

        configurationContent = (
            <>
                {relevanceLabel}
                {relevanceSlider}
                {comparisonLabel}
                {comparisonSlider}
                {queryLabel}
                <ReferenceQueryInput />
                {querySlider}
                {favoriteLabel}
                <FavoriteColumnBox />
                {favoriteSlider}
                <RightAlign>{applyButton}</RightAlign>
            </>
        );
    }

    // output
    return <div ref={ref}>{configurationContent}</div>;
};
